'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '../../toast/ToastProvider';
import { notifyEnrollmentActionFailure } from './enrollment-action-feedback';
import {
  advertisedRoutesPayload,
  type InternalNetworkTunnelArtifacts,
} from './internal-connection.model';

export type InternalNetworkTunnelEnrollmentCallbacks = {
  onCreateTunnelEnrollment: (payload: {
    advertisedRoutes?: string[];
  }) => Promise<InternalNetworkTunnelArtifacts>;
  onReprovisionTunnelEnrollment: (
    enrollmentId: string,
  ) => Promise<InternalNetworkTunnelArtifacts>;
  onRevokeTunnelEnrollment: (enrollmentId: string) => Promise<unknown>;
};

export type InternalNetworkTunnelArtifactsDialogState = {
  title: string;
  artifacts: InternalNetworkTunnelArtifacts;
};

export type InternalNetworkTunnelEnrollmentActions = {
  artifactsDialog: InternalNetworkTunnelArtifactsDialogState | null;
  advertisedRoutes: string[];
  setAdvertisedRoutes: (routes: string[]) => void;
  isCreating: boolean;
  reprovisioningEnrollmentId: string | null;
  revokingEnrollmentId: string | null;
  createTunnelEnrollment: () => Promise<void>;
  reprovisionTunnelEnrollment: (enrollmentId: string) => Promise<void>;
  revokeTunnelEnrollment: (enrollmentId: string) => Promise<void>;
  closeArtifactsDialog: () => void;
};

export function useInternalNetworkTunnelEnrollmentActions({
  onCreateTunnelEnrollment,
  onReprovisionTunnelEnrollment,
  onRevokeTunnelEnrollment,
}: InternalNetworkTunnelEnrollmentCallbacks): InternalNetworkTunnelEnrollmentActions {
  const t = useTranslations(
    'designSystem.networkSettings.internalConnection.tunnel',
  );
  const tCommon = useTranslations('designSystem.common');
  const [artifactsDialog, setArtifactsDialog] =
    useState<InternalNetworkTunnelArtifactsDialogState | null>(null);
  const [advertisedRoutes, setAdvertisedRoutes] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [reprovisioningEnrollmentId, setReprovisioningEnrollmentId] = useState<
    string | null
  >(null);
  const [revokingEnrollmentId, setRevokingEnrollmentId] = useState<
    string | null
  >(null);

  const createTunnelEnrollment = async () => {
    setIsCreating(true);

    try {
      const artifacts = await onCreateTunnelEnrollment(
        advertisedRoutesPayload(advertisedRoutes),
      );

      setArtifactsDialog({ title: t('createSuccessTitle'), artifacts });
      toasterApi.success({ title: t('createSuccessTitle') });
    } catch (error: unknown) {
      notifyEnrollmentActionFailure({
        error,
        errorTitle: tCommon('error'),
        operation: 'create internal network tunnel enrollment',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const reprovisionTunnelEnrollment = async (enrollmentId: string) => {
    setReprovisioningEnrollmentId(enrollmentId);

    try {
      const artifacts = await onReprovisionTunnelEnrollment(enrollmentId);

      setArtifactsDialog({ title: t('reprovisionSuccessTitle'), artifacts });
      toasterApi.success({ title: t('reprovisionSuccessTitle') });
    } catch (error: unknown) {
      notifyEnrollmentActionFailure({
        error,
        errorTitle: tCommon('error'),
        operation: 'reprovision internal network tunnel enrollment',
      });
    } finally {
      setReprovisioningEnrollmentId(null);
    }
  };

  const revokeTunnelEnrollment = async (enrollmentId: string) => {
    setRevokingEnrollmentId(enrollmentId);

    try {
      await onRevokeTunnelEnrollment(enrollmentId);
      toasterApi.success({ title: t('revokeSuccessTitle') });
    } catch (error: unknown) {
      notifyEnrollmentActionFailure({
        error,
        errorTitle: tCommon('error'),
        operation: 'revoke internal network tunnel enrollment',
      });
    } finally {
      setRevokingEnrollmentId(null);
    }
  };

  return {
    artifactsDialog,
    advertisedRoutes,
    setAdvertisedRoutes,
    isCreating,
    reprovisioningEnrollmentId,
    revokingEnrollmentId,
    createTunnelEnrollment,
    reprovisionTunnelEnrollment,
    revokeTunnelEnrollment,
    closeArtifactsDialog: () => setArtifactsDialog(null),
  };
}
